import { useState, useEffect } from "react";
import { Row, Col, Button, Input, Space, Tooltip } from "antd";
import {
  PlusOutlined,
  ContainerOutlined,
  WalletOutlined,
  PrinterOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

import { getMemberApi } from "../../services/Member/MemberApis";

import DataTable from "../DataTable";
import { AddPaymentModal } from "../ModalForm";
import AddRefundModal from "../ModalForm/Member/AddRefundModal";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import StatementModal from "../ModalForm/Member/StatementModal";
import ReceiptModal from "../ModalForm/Member/ReceiptModal";
import {
  formatTransactionDateTime,
} from "../../utils/sastDate";

dayjs.extend(utc);

const { Search } = Input;

const isNupayDebiCheck = (record: any): boolean =>
  String(record?.PaymentProvider ?? "").toLowerCase() === "nupay" &&
  String(record?.Type ?? "") === "DebiCheck";

/** Prefer API field; fall back to Reference when it already holds the raw Tran ID. */
const resolveMandateRequestTranId = (record: any): string => {
  const direct = String(record?.MandateRequestTransactionID ?? "").trim();
  if (direct) return direct;

  const ref = String(record?.Reference ?? "").trim();
  if (isNupayDebiCheck(record) && ref && !ref.includes("|")) {
    return ref;
  }
  return "";
};

interface IPaymentProps {
  payments: any;
  member: any;
  memberId: any;
  handleAdd: Function;
  handleDelete: Function;
  policyId: any;
}

const PaymentTable: React.FC<IPaymentProps> = ({
  payments,
  member,
  memberId,
  handleAdd,
  handleDelete,
  policyId,
}) => {
  const [originalData, setOriginalData] = useState(payments);
  const [all, setAll] = useState([]); //All Tab
  const [accountBalance, setAccountBalance] = useState(
    member?.Policy?.OutstandingPremium || 0
  );
  const [state, setState] = useState({
    payment: false,
    refund: false,
    receipt: false,
    statement: false,
    loading: true,
    activeKey: "1",
    pagination: {
      pageSize: 15,
      position: ["bottomRight"],
      total: 0,
      current: 1,
      hideOnSinglePage: true,
    },
    searchItem: "",
  });
  const [receipt, setReceipt] = useState({});

  const showPaymentModal = () => {
    setState((prevState) => ({
      ...prevState,
      payment: !prevState.payment,
    }));
  };

  const showRefundModal = () => {
    setState((prevState) => ({
      ...prevState,
      refund: !prevState.refund,
    }));
  };

  const showStatementModal = () => {
    setState((prevState) => ({
      ...prevState,
      statement: !prevState.statement,
    }));
  };

  const showReceiptModal = (data: any) => {
    setState((prevState) => ({
      ...prevState,
      receipt: !prevState.receipt,
    }));
    setReceipt({ Payment: data, ...(member || {}) });
  };

  const fetchAll = async (params = {}) => {
    try {
      const all = payments || [];
      console.log("Payments: ", payments);
      const filteredData = all.filter(
        (item: any) => item?.Amount && !item.Amount.includes("-")
      );
      const pagination = state.pagination;
      pagination.total = filteredData.length;

      setState({
        ...state,
        loading: false,
        pagination,
        ...params,
      });

      setOriginalData(filteredData);
      setAll(filteredData);
    } catch (err) {
      console.log(err);
    }
  };

  const onSearch = (e: any) => {
    const value = e.target.value;
    if (value !== "") {
      const filteredData = originalData.filter((row: any) =>
        Object.values(row)
          .join(" ")
          .toLowerCase()
          .includes(value.toLowerCase())
      );
      setAll(filteredData);
    } else {
      setAll(originalData);
    }
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const pager = { ...state.pagination };
    pager.current = pagination.current;
    setState({
      ...state,
      pagination: pager,
    });

    const params = {
      pageSize: pagination.pageSize,
      page: pagination.current,
      sortField: sorter.field,
      sortOrder: sorter.order,
      ...filters,
    };

    fetchAll(params);
  };

  useEffect(() => {
    getMemberApi(String(memberId))
      .then((res) => {
        setAccountBalance(res?.data?.Policy?.OutstandingPremium || 0);
      })
      .catch((err) => {
        console.log(err);
        setAccountBalance(0);
      });

    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, member]);

  const columns = [
    {
      title: "Payment ID",
      dataIndex: "id",
      render: (id: string) => id.toUpperCase(),
    },
    {
      title: "Payment Provider",
      dataIndex: "PaymentProvider",
    },
    {
      title: "Payment Date",
      dataIndex: "Date",
      render: (date: Date) => formatTransactionDateTime(date),
      sorter: (a: any, b: any) =>
        dayjs.utc(a.Date).unix() - dayjs.utc(b.Date).unix(),
    },
    {
      title: "Payment Processed",
      dataIndex: "DateProcessed",
      render: (date: Date) => formatTransactionDateTime(date),
      sorter: (a: any, b: any) =>
        dayjs.utc(a.DateProcessed).unix() - dayjs.utc(b.DateProcessed).unix(),
    },
    {
      title: "Payment Type",
      dataIndex: "Type",
    },
    {
      title: "Payment Amount",
      dataIndex: "Amount",
    },
    {
      title: "Payment Reference",
      dataIndex: "Reference",
      render: (value: string | null | undefined, record: any) => {
        const ref = String(value ?? "").trim();
        if (!ref) return "—";

        // NuPay DebiCheck stores Tran ID in Reference after migration — don't repeat it
        const tranId = resolveMandateRequestTranId(record);
        if (tranId && ref === tranId) return "—";

        return ref;
      },
    },
    {
      title: "Mandate Request Tran ID",
      dataIndex: "MandateRequestTransactionID",
      render: (_: string | null | undefined, record: any) =>
        resolveMandateRequestTranId(record) || "—",
    },
    {
      dataIndex: "Action",
      render: (_: any, record: any) => (
        <Space size="middle">
          <Tooltip title="Print Receipt">
            <PrinterOutlined
              onClick={() => showReceiptModal(record)}
              style={{ color: "green" }}
            />
          </Tooltip>
          <Tooltip title="Remove Payment">
            <DeleteOutlined
              onClick={() => handleDelete(record.id, record.PolicyID)}
              style={{ color: "red" }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const tabPanes = [
    {
      key: "1",
      title: "All",
      content: (
        <DataTable
          persistKey="payments"
          dataSource={all}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count: all.length,
    },
  ];

  return (
    <>
      <Row>
        <Col span={24}>
          <div className="interactions">
            <div style={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
              <Button
                onClick={showPaymentModal}
                size="large"
              >
                <PlusOutlined style={{ paddingRight: "5px" }} />
                Add Payment
              </Button>
              <Button
                onClick={showRefundModal}
                size="large"
                type="default"
              >
                <WalletOutlined style={{ paddingRight: "5px" }} />
                Process Refund
              </Button>
            </div>

            <div style={{ display: "flex" }}>
              <h3
                style={
                  accountBalance < 0 ? { color: "red" } : { color: "green" }
                }
              >
                <WalletOutlined /> Account Balance: R {accountBalance}
              </h3>
              <Tooltip title="Download Statement">
                <ContainerOutlined
                  onClick={showStatementModal}
                  style={{ marginLeft: "16px" }}
                />
              </Tooltip>
            </div>
            <div>
              <Search
                placeholder="Search"
                size="large"
                onChange={(e) => onSearch(e)}
                style={{ width: "100%", margin: "24px 0" }}
              />
            </div>
          </div>
          {tabPanes[0].content}

          {state.payment && (
            <AddPaymentModal
              show={state.payment}
              memberId={memberId}
              showModal={showPaymentModal}
              handleAdd={handleAdd}
              policyId={policyId}
            />
          )}

          {state.refund && (
            <AddRefundModal
              show={state.refund}
              memberId={memberId}
              showModal={showRefundModal}
              handleAdd={handleAdd}
              policyId={policyId}
            />
          )}

          {state.receipt && (
            <ReceiptModal
              show={state.receipt}
              receipt={receipt}
              showModal={showReceiptModal}
              handleAdd={handleAdd}
            />
          )}

          {state.statement && (
            <StatementModal
              show={state.statement}
              member={member}
              payments={payments}
              showModal={showStatementModal}
              accountBalance={accountBalance}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default PaymentTable;
